import type {
  CompiledCatalogJson,
  CompiledEntityDisplay,
  CompiledFrameworkEntityAttribute,
  CompiledFrameworkEntityType,
  CompiledFrameworkEntityValueOption,
  CompiledFrameworkStructure,
  FrameworkEntityAliases,
  FrameworkEntityMetadataFilterContext,
  FrameworkEntityValueType,
  EntityAuthoringFieldOptions as MetadataFieldOptions,
  EntityAuthoringFieldOption as MetadataOption,
} from "@repo/shared";
import {
  buildFrameworkEntityAliasesFromStructure,
  compareCatalogAttributes,
  FRAMEWORK_ENTITY_DEFINITION_ROLES,
  getRequiredEntityAlias,
  selectCatalogAttributes,
  selectCatalogMetadataFilterAttributes,
  selectCatalogOwnerAttributes,
} from "@repo/shared";

export { getRootFolderEntityTypeKeys } from "@repo/shared";

import type { TreeItemType } from "../types";

export type ProjectFrameworkCatalog = Pick<CompiledCatalogJson, "entityTypes" | "attributes">;

type CatalogAttribute = CompiledFrameworkEntityAttribute;

export type CatalogMetadataField = {
  fieldId: string;
  label: string;
  persistenceKey: string;
  valueType: FrameworkEntityValueType;
  options?: MetadataFieldOptions;
  filterContexts?: FrameworkEntityMetadataFilterContext[];
};

export type CatalogEntityTypeDisplay = {
  name: string;
  article: "a" | "an" | "the";
};

const PROJECT_TYPE_DISPLAY: CatalogEntityTypeDisplay = { name: "item", article: "an" };

function activeEntityTypes(catalog: ProjectFrameworkCatalog | null | undefined) {
  return (catalog?.entityTypes ?? []).filter((type) => !type.retiredAt);
}

export function getCatalogEntityType(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string
): CompiledFrameworkEntityType | null {
  return activeEntityTypes(catalog).find((type) => type.entityTypeKey === entityTypeKey) ?? null;
}

export function catalogHasEntityType(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string
): boolean {
  return getCatalogEntityType(catalog, entityTypeKey) !== null;
}

/** Which of the element-level actions the UI may offer for a type. */
export type CatalogEntityTypeCapabilities = {
  canRename: boolean;
  canDelete: boolean;
};

/**
 * The single source of truth for whether the UI may offer Rename and Delete on an element.
 *
 * A singleton type exists exactly once per project under a fixed name, so renaming one is never
 * legal. Deleting is legal in exactly one case: the project holds more than one active row of the
 * type, which happens when a framework author flips `isSingleton` on a type projects already
 * hold. Removing the surplus is how a person repairs that, and the backend allows it whatever
 * `nonDeletable` says — so the UI must offer it, while still hiding Delete on the last row.
 * `nonDeletable` is an independent pin that blocks deletion for any type.
 */
export function getCatalogEntityTypeCapabilities(
  entityType: CompiledFrameworkEntityType | null | undefined,
  activeCountOfType: number
): CatalogEntityTypeCapabilities {
  const isSingleton = entityType?.isSingleton === true;
  return {
    canRename: !isSingleton,
    canDelete: isSingleton ? activeCountOfType > 1 : entityType?.nonDeletable !== true,
  };
}

function listCatalogAttributes(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string,
  role: CatalogAttribute["role"]
): CatalogAttribute[] {
  return selectCatalogAttributes(catalog?.attributes, entityTypeKey, role);
}

function toMetadataOptions(
  options: CompiledFrameworkEntityValueOption[] | undefined
): MetadataFieldOptions | undefined {
  if (!options?.length) return undefined;
  return {
    type: "flat",
    options: options
      .filter((option) => !option.disabledAt)
      .sort((left, right) => left.listOrder - right.listOrder)
      .map<MetadataOption>((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
      })),
  };
}

function toMetadataField(attribute: CatalogAttribute): CatalogMetadataField {
  return {
    fieldId: attribute.fieldId,
    label: attribute.label,
    persistenceKey: attribute.persistenceKey,
    valueType: attribute.valueType,
    options: toMetadataOptions(attribute.options),
    filterContexts: attribute.filterContexts,
  };
}

/**
 * Every attribute an entity type declares, whatever its role and whether or not it is offered
 * on create — the whole vocabulary of the type, as an explanatory surface needs it. Authoring
 * surfaces want `getCatalogMetadataFields` instead: it filters to what may be edited there.
 */
export function getCatalogEntityAttributeFields(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string
): CatalogMetadataField[] {
  return FRAMEWORK_ENTITY_DEFINITION_ROLES.flatMap((role) =>
    listCatalogAttributes(catalog, entityTypeKey, role)
  )
    .sort(compareCatalogAttributes)
    .map(toMetadataField);
}

export function getCatalogOwnerFields(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string
): CatalogAttribute[] {
  return selectCatalogOwnerAttributes(catalog?.attributes, entityTypeKey);
}

export function getCatalogMetadataFields(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string,
  options?: { includeNonAuthoringFields?: boolean; folderBadgesOnly?: boolean }
): CatalogMetadataField[] {
  return listCatalogAttributes(catalog, entityTypeKey, "attribute")
    .filter((attribute) => {
      if (!options?.includeNonAuthoringFields && attribute.showOnCreate === false) {
        return false;
      }
      if (options?.folderBadgesOnly && attribute.showAsFolderBadge !== true) {
        return false;
      }
      return true;
    })
    .map(toMetadataField);
}

export function getCatalogMetadataFilterFields(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string,
  context: FrameworkEntityMetadataFilterContext
): CatalogMetadataField[] {
  return selectCatalogMetadataFilterAttributes(catalog?.attributes, entityTypeKey, context).map(
    toMetadataField
  );
}

export function getCatalogCollectionBadgeField(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string
): CatalogMetadataField | null {
  const field =
    listCatalogAttributes(catalog, entityTypeKey, "attribute").find(
      (attribute) => attribute.showAsCollectionBadge === true
    ) ?? null;
  return field ? toMetadataField(field) : null;
}

export function getCatalogCollectionBadgeLabel(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string
): string | null {
  return getCatalogEntityType(catalog, entityTypeKey)?.collectionBadgeLabel ?? null;
}

export function buildEntityDisplayMap(
  structure: Pick<CompiledFrameworkStructure, "entityDisplays"> | null
): Map<string, CompiledEntityDisplay> {
  if (!structure) return new Map();
  const displays = Array.isArray(structure.entityDisplays) ? structure.entityDisplays : [];
  return new Map(
    displays
      .filter(
        (display): display is CompiledEntityDisplay =>
          Boolean(display) && typeof display.entityTypeKey === "string"
      )
      .map((display) => [display.entityTypeKey, display])
  );
}

export function getEntityAlias(
  entityTypeKey: string,
  displays: Map<string, CompiledEntityDisplay>
): string {
  return getRequiredEntityAlias(
    buildFrameworkEntityAliasesFromStructure({
      entityDisplays: Array.from(displays.values()),
    })!,
    entityTypeKey
  );
}

export function buildFrameworkEntityAliases(
  structure: Pick<CompiledFrameworkStructure, "entityDisplays"> | null
): FrameworkEntityAliases | null {
  return buildFrameworkEntityAliasesFromStructure(structure);
}

function defaultEntityTypeDisplayName(label: string): string {
  return label.replace(/[A-Za-z]+(?:'[A-Za-z]+)?/g, (word) =>
    /^[A-Z0-9]+$/.test(word) ? word : word.toLowerCase()
  );
}

function defaultEntityTypeDisplayArticle(name: string): "a" | "an" {
  const firstLetter = name
    .trim()
    .match(/[A-Za-z]/)?.[0]
    ?.toLowerCase();
  return firstLetter && ["a", "e", "i", "o", "u"].includes(firstLetter) ? "an" : "a";
}

export function toCanonicalCatalogEntityTypeKey(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityType: string | null | undefined
): string | null {
  if (!entityType || !catalogHasEntityType(catalog, entityType)) {
    return null;
  }
  return entityType;
}

export function getCatalogEntityTypeDisplay(
  catalog: ProjectFrameworkCatalog | null | undefined,
  displays: Map<string, CompiledEntityDisplay>,
  entityType: string | null | undefined
): CatalogEntityTypeDisplay {
  if (!entityType) return PROJECT_TYPE_DISPLAY;

  const canonical = toCanonicalCatalogEntityTypeKey(catalog, entityType);
  if (!canonical) return { name: entityType, article: "a" };

  const display = displays.get(canonical);
  if (display) {
    return {
      name: defaultEntityTypeDisplayName(display.label),
      article: display.article,
    };
  }

  const catalogType = getCatalogEntityType(catalog, canonical);
  const name = defaultEntityTypeDisplayName(catalogType?.label ?? canonical);
  return {
    name,
    article: defaultEntityTypeDisplayArticle(name),
  };
}

export function getTreeItemType(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string
): TreeItemType {
  const treeItemKind = getCatalogEntityType(catalog, entityTypeKey)?.treeItemKind;
  return (
    typeof treeItemKind === "string" && treeItemKind.length > 0 ? treeItemKind : "measure"
  ) as TreeItemType;
}

export function getEntityFolderBadges(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string,
  entity: Record<string, unknown>
): string[] {
  return getCatalogMetadataFields(catalog, entityTypeKey, {
    includeNonAuthoringFields: true,
    folderBadgesOnly: true,
  })
    .map((field) => entity[field.persistenceKey])
    .filter((value): value is string => typeof value === "string" && value.length > 0);
}

export function getEntityMetadataMap(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string,
  entity: Record<string, unknown>
): Record<string, string | undefined> {
  return Object.fromEntries(
    getCatalogMetadataFields(catalog, entityTypeKey, {
      includeNonAuthoringFields: true,
    }).map((field) => [
      field.persistenceKey,
      typeof entity[field.persistenceKey] === "string"
        ? String(entity[field.persistenceKey])
        : undefined,
    ])
  );
}
